// Grammar Units 13 Data - Unit 13: Advanced Grammar Structures
// Based on Ch13.jsx
// Organized for Supabase migration and systematic data management

export const grammarUnits13Data = [
  {
    id: 13,
    title: { traditional: '進階文法結構', simplified: '进阶文法结构', english: 'Advanced Grammar Structures' },
    subtitle: { traditional: '掌握複雜的英文文法結構', simplified: '掌握复杂的英文文法结构', english: 'Master complex English grammar structures' },
    description: { traditional: '系統性學習英文進階文法結構，包括倒裝句、強調句、省略句、複雜句型等。', simplified: '系统性学习英文进阶文法结构，包括倒装句、强调句、省略句、复杂句型等。', english: 'Systematic learning of advanced English grammar structures including inversion, emphasis, ellipsis, complex sentence patterns and more.' },
    icon: '🏗️',
    color: 'from-pink-500 to-rose-600',
    status: 'notStarted',
    difficulty: 'advanced',
    estimatedHours: 5,
    content: {
      overview: {
        title: "進階文法結構概覽",
        description: "進階文法結構包括各種複雜的句型，這些結構能夠讓表達更加豐富和準確。",
        structures: [
          { type: "倒裝句", description: "將句子成分的順序顛倒，用於強調或特殊語境" },
          { type: "強調句", description: "使用特殊結構來強調句子的某個部分" },
          { type: "省略句", description: "省略某些句子成分，使表達更簡潔" },
          { type: "複雜句型", description: "包含多個子句的複雜句子結構" },
          { type: "特殊句型", description: "具有特殊語法功能的句型" }
        ]
      },
      inversion: {
        title: "倒裝句 (Inversion)",
        description: "倒裝句是將句子成分的順序顛倒，通常用於強調或特殊語境。",
        types: [
          {
            type: "否定詞倒裝",
            description: "當句子以否定詞開頭時，主詞和動詞的位置會顛倒",
            examples: [
              {
                normal: "I have never seen such a beautiful sunset.",
                inverted: "Never have I seen such a beautiful sunset.",
                explanation: "never 是否定詞，放在句首時需要倒裝"
              },
              {
                normal: "She rarely goes to the movies.",
                inverted: "Rarely does she go to the movies.",
                explanation: "rarely 是頻率副詞，放在句首時需要倒裝"
              },
              {
                normal: "He seldom visits his parents.",
                inverted: "Seldom does he visit his parents.",
                explanation: "seldom 是頻率副詞，放在句首時需要倒裝"
              }
            ],
            negative_words: [
              "never", "rarely", "seldom", "hardly", "scarcely", 
              "barely", "not only", "no sooner", "not until", "under no circumstances"
            ]
          },
          {
            type: "地點副詞倒裝",
            description: "當地點副詞放在句首時，主詞和動詞的位置會顛倒",
            examples: [
              {
                normal: "The old church stands on the hill.",
                inverted: "On the hill stands the old church.",
                explanation: "地點副詞片語放在句首時需要倒裝"
              },
              {
                normal: "A beautiful garden lies behind the house.",
                inverted: "Behind the house lies a beautiful garden.",
                explanation: "地點副詞片語放在句首時需要倒裝"
              }
            ]
          },
          {
            type: "條件句倒裝",
            description: "在虛擬語氣條件句中，可以省略 if 並倒裝",
            examples: [
              {
                normal: "If I were you, I would accept the offer.",
                inverted: "Were I you, I would accept the offer.",
                explanation: "省略 if 後，were 移到主詞前"
              },
              {
                normal: "If he had known, he would have come.",
                inverted: "Had he known, he would have come.",
                explanation: "省略 if 後，had 移到主詞前"
              },
              {
                normal: "If it should rain, the game will be cancelled.",
                inverted: "Should it rain, the game will be cancelled.",
                explanation: "省略 if 後，should 移到主詞前"
              }
            ]
          }
        ]
      },
      emphasis: {
        title: "強調句 (Emphasis)",
        description: "強調句使用特殊結構來強調句子的某個部分。",
        types: [
          {
            type: "It is/was...that...",
            description: "使用 it is/was...that... 結構來強調句子的某個部分",
            examples: [
              {
                original: "I met John at the station yesterday.",
                emphasized_subject: "It was I who met John at the station yesterday.",
                emphasized_object: "It was John whom I met at the station yesterday.",
                emphasized_place: "It was at the station that I met John yesterday.",
                emphasized_time: "It was yesterday that I met John at the station."
              },
              {
                original: "She bought the car last week.",
                emphasized_subject: "It was she who bought the car last week.",
                emphasized_object: "It was the car that she bought last week.",
                emphasized_time: "It was last week that she bought the car."
              }
            ]
          },
          {
            type: "What...is/was...",
            description: "使用 what...is/was... 結構來強調",
            examples: [
              {
                original: "I need money.",
                emphasized: "What I need is money.",
                explanation: "強調 what 後面的內容"
              },
              {
                original: "She wants to travel.",
                emphasized: "What she wants is to travel.",
                explanation: "強調 what 後面的內容"
              }
            ]
          },
          {
            type: "重複使用助動詞",
            description: "重複使用助動詞來強調",
            examples: [
              {
                original: "I do like this movie.",
                explanation: "do 用來強調 like"
              },
              {
                original: "She did finish the work.",
                explanation: "did 用來強調 finish"
              },
              {
                original: "They have been working hard.",
                explanation: "have 用來強調 been working"
              }
            ]
          }
        ]
      },
      ellipsis: {
        title: "省略句 (Ellipsis)",
        description: "省略句是省略某些句子成分，使表達更簡潔。",
        types: [
          {
            type: "主詞省略",
            description: "在祈使句或某些特定語境中省略主詞",
            examples: [
              {
                complete: "You should go now.",
                ellipsis: "Go now.",
                explanation: "祈使句中省略主詞 you"
              },
              {
                complete: "You must be careful.",
                ellipsis: "Be careful.",
                explanation: "祈使句中省略主詞 you"
              }
            ]
          },
          {
            type: "動詞省略",
            description: "在並列句中省略重複的動詞",
            examples: [
              {
                complete: "Tom likes coffee and Mary likes coffee too.",
                ellipsis: "Tom likes coffee and Mary does too.",
                explanation: "省略重複的動詞片語"
              },
              {
                complete: "I can swim and she can swim.",
                ellipsis: "I can swim and she can too.",
                explanation: "省略重複的動詞"
              }
            ]
          },
          {
            type: "主詞和動詞省略",
            description: "在回答問題或特定語境中省略主詞和動詞",
            examples: [
              {
                question: "Are you going to the party?",
                complete_answer: "Yes, I am going to the party.",
                ellipsis_answer: "Yes, I am.",
                explanation: "省略重複的部分"
              },
              {
                question: "Do you like this movie?",
                complete_answer: "Yes, I do like this movie.",
                ellipsis_answer: "Yes, I do.",
                explanation: "省略重複的部分"
              }
            ]
          }
        ]
      },
      complex_structures: {
        title: "複雜句型 (Complex Structures)",
        description: "複雜句型包含多個子句，能夠表達更複雜的語義關係。",
        types: [
          {
            type: "名詞子句",
            description: "在句子中充當名詞功能的子句",
            examples: [
              {
                structure: "主詞 + 動詞 + that + 子句",
                example: "I know that he is coming.",
                explanation: "that he is coming 是名詞子句，作 know 的受詞"
              },
              {
                structure: "主詞 + 動詞 + what + 子句",
                example: "I don't know what he wants.",
                explanation: "what he wants 是名詞子句，作 know 的受詞"
              },
              {
                structure: "主詞 + 動詞 + whether + 子句",
                example: "I wonder whether he will come.",
                explanation: "whether he will come 是名詞子句，作 wonder 的受詞"
              }
            ]
          },
          {
            type: "形容詞子句",
            description: "用來修飾名詞的子句",
            examples: [
              {
                structure: "名詞 + who/which/that + 子句",
                example: "The man who lives next door is a doctor.",
                explanation: "who lives next door 修飾 the man"
              },
              {
                structure: "名詞 + whose + 子句",
                example: "The woman whose car was stolen called the police.",
                explanation: "whose car was stolen 修飾 the woman"
              },
              {
                structure: "名詞 + where + 子句",
                example: "This is the place where I was born.",
                explanation: "where I was born 修飾 the place"
              }
            ]
          },
          {
            type: "副詞子句",
            description: "用來修飾動詞、形容詞或副詞的子句",
            examples: [
              {
                type: "時間副詞子句",
                example: "When I arrived, the party had already started.",
                explanation: "When I arrived 修飾 had started"
              },
              {
                type: "原因副詞子句",
                example: "I stayed home because it was raining.",
                explanation: "because it was raining 修飾 stayed"
              },
              {
                type: "條件副詞子句",
                example: "If it rains, I will stay home.",
                explanation: "If it rains 修飾 will stay"
              }
            ]
          }
        ]
      },
      special_patterns: {
        title: "特殊句型 (Special Patterns)",
        description: "具有特殊語法功能的句型。",
        patterns: [
          {
            type: "There is/are...",
            description: "表示存在或發生的句型",
            examples: [
              "There is a book on the table.",
              "There are many students in the classroom.",
              "There was a meeting yesterday.",
              "There will be a party next week."
            ]
          },
          {
            type: "It is + 形容詞 + to + 動詞",
            description: "表示評價或看法的句型",
            examples: [
              "It is important to study hard.",
              "It is difficult to learn English.",
              "It is easy to make mistakes.",
              "It is necessary to practice regularly."
            ]
          },
          {
            type: "主詞 + 動詞 + 受詞 + 補語",
            description: "包含受詞補語的句型",
            examples: [
              "I find this book interesting.",
              "They consider him a good teacher.",
              "We made her happy.",
              "The news kept us informed."
            ]
          },
          {
            type: "主詞 + 動詞 + 間接受詞 + 直接受詞",
            description: "包含雙受詞的句型",
            examples: [
              "I gave him a book.",
              "She sent me a letter.",
              "They bought us a gift.",
              "He told me the truth."
            ]
          }
        ]
      },
      exercises: {
        title: "練習題",
        sections: [
          {
            name: "倒裝句練習",
            questions: [
              {
                question: "1. __________ have I seen such a beautiful sunset.",
                options: {
                  A: "Never",
                  B: "Always",
                  C: "Sometimes",
                  D: "Often"
                },
                answer: "A",
                explanation: "never 是否定詞，放在句首時需要倒裝"
              },
              {
                question: "2. __________ the hill stands an old church.",
                options: {
                  A: "On",
                  B: "In",
                  C: "At",
                  D: "By"
                },
                answer: "A",
                explanation: "地點副詞片語放在句首時需要倒裝"
              }
            ]
          },
          {
            name: "強調句練習",
            questions: [
              {
                question: "1. __________ was John whom I met at the station.",
                options: {
                  A: "It",
                  B: "That",
                  C: "What",
                  D: "Which"
                },
                answer: "A",
                explanation: "使用 it is/was...that... 結構來強調"
              },
              {
                question: "2. __________ I need is money.",
                options: {
                  A: "It",
                  B: "That",
                  C: "What",
                  D: "Which"
                },
                answer: "C",
                explanation: "使用 what...is/was... 結構來強調"
              }
            ]
          }
        ]
      }
    }
  }
];

// Export individual units for specific access
export const getUnitById = (unitId) => {
  return grammarUnits13Data.find(unit => unit.id === unitId);
};

// Export units by difficulty
export const getUnitsByDifficulty = (difficulty) => {
  return grammarUnits13Data.filter(unit => unit.difficulty === difficulty);
};

// Export completed units
export const getCompletedUnits = () => {
  return grammarUnits13Data.filter(unit => unit.status === 'completed');
};

// Default export
export default grammarUnits13Data;
